import type { CreateAgentDataInput } from "@/graphql/generated/operations";
import type { Agent } from "@/components/match/agents";

// The Stammdaten editor's form state for one AI agent, and its translation to genesis's input.
// Every prose field carries both languages; a row left blank in all of its fields is dropped on
// save rather than sent, because genesis refuses a list entry that says nothing.

export interface LocalizedDraft {
  german: string;
  english: string;
}

export interface AgentToolDraft {
  name: string;
  description: LocalizedDraft;
}

export interface AgentDraft {
  name: string;
  vendor: string;
  url: string;
  model: string;
  role: LocalizedDraft;
  covers: LocalizedDraft[];
  tools: AgentToolDraft[];
  integrations: string[];
  guardrails: LocalizedDraft[];
}

export const EMPTY_LOCALIZED: LocalizedDraft = { german: "", english: "" };

export const EMPTY_AGENT_DRAFT: AgentDraft = {
  name: "",
  vendor: "",
  url: "",
  model: "",
  role: EMPTY_LOCALIZED,
  covers: [],
  tools: [],
  integrations: [],
  guardrails: [],
};

type LocalizedTextLike = { german?: string | null; english?: string | null };

const localizedDraft = (text: LocalizedTextLike | null | undefined): LocalizedDraft => ({
  german: text?.german ?? "",
  english: text?.english ?? "",
});

export function agentToDraft(agent: Agent): AgentDraft {
  return {
    name: agent.name,
    vendor: agent.vendor,
    url: agent.url ?? "",
    model: agent.model ?? "",
    role: localizedDraft(agent.role),
    covers: agent.covers.map(localizedDraft),
    tools: agent.tools.map((t) => ({ name: t.name, description: localizedDraft(t.description) })),
    integrations: [...agent.integrations],
    guardrails: agent.guardrails.map(localizedDraft),
  };
}

/** Equality key for dirty-checking; whitespace-only edits count as edits, like every other form. */
export function draftSignature(draft: AgentDraft): string {
  return JSON.stringify(draft);
}

const blank = (value: string) => value.trim().length === 0;
const blankText = (text: LocalizedDraft) => blank(text.german) && blank(text.english);

/**
 * Prose as it is sent: surrounding whitespace stripped and `\r\n` written as `\n`, the way genesis
 * normalises it. Line breaks inside prose are kept: genesis stores them, and the sheet renders them.
 */
export function proseValue(value: string): string {
  return value.replace(/\r\n/g, "\n").trim();
}

// Unicode category Cc, which genesis refuses inside an Agent string (Java Character.isISOControl:
// C0, DEL and C1), except a line feed inside prose. Written as a code-point test, not a regex, so
// the control range never appears as literal characters in the source.
const LINE_FEED = 0x0a;
const isControl = (code: number) => code <= 0x1f || (code >= 0x7f && code <= 0x9f);
const hasControlCharacter = (value: string, allowLineFeed = false) =>
  [...value].some((ch) => {
    const code = ch.codePointAt(0) ?? 0;
    return isControl(code) && !(allowLineFeed && code === LINE_FEED);
  });

const ALLOWED_URL_SCHEMES = new Set(["http", "https"]);
const MAX_URL_LENGTH = 128;

/**
 * Mirrors genesis's FieldRules.websiteUrl on the stripped value: at most 128 code points, http,
 * https or no scheme at all (genesis reads that as https), and no whitespace inside.
 */
export function isAcceptableUrl(raw: string): boolean {
  const value = raw.trim();
  if (!value) return true;
  if ([...value].length > MAX_URL_LENGTH) return false;
  const colon = value.indexOf(":");
  const slash = value.indexOf("/");
  // A colon after the first slash is part of a path or port, not a scheme.
  const hasScheme = colon > 0 && (slash < 0 || colon < slash);
  if (hasScheme && !ALLOWED_URL_SCHEMES.has(value.slice(0, colon).toLowerCase())) return false;
  return !/\s/.test(value);
}

/** Why this draft cannot be saved yet, or null. Mirrors genesis's own refusals. */
export type AgentDraftProblem =
  | "name"
  | "vendor"
  | "role"
  | "toolName"
  | "toolDescription"
  | "url"
  | "controlCharacters";

export function draftProblem(draft: AgentDraft): AgentDraftProblem | null {
  if (blank(draft.name)) return "name";
  if (blank(draft.vendor)) return "vendor";
  if (blankText(draft.role)) return "role";
  // A row blank in every field is dropped on save; any other tool needs both its parts.
  const tools = draft.tools.filter((t) => !blank(t.name) || !blankText(t.description));
  if (tools.some((t) => blank(t.name))) return "toolName";
  if (tools.some((t) => blankText(t.description))) return "toolDescription";
  if (!isAcceptableUrl(draft.url)) return "url";
  // Checked on what is actually sent (stripped, `\r\n` normalised). Product tokens are single-line:
  // any control character is refused. Prose may break lines; a tab or any other one is refused.
  const input = draftToInput(draft);
  const tokens = [
    input.name,
    input.vendor,
    input.url ?? "",
    input.model ?? "",
    ...input.tools.map((t) => t.name),
    ...input.integrations,
  ];
  const prose = [input.role, ...input.covers, ...input.guardrails, ...input.tools.map((t) => t.description)].flatMap(
    (p) => [p.german ?? "", p.english ?? ""],
  );
  if (tokens.some((v) => hasControlCharacter(v)) || prose.some((v) => hasControlCharacter(v, true))) {
    return "controlCharacters";
  }
  return null;
}

const text = (value: LocalizedDraft) => ({
  german: proseValue(value.german) || null,
  english: proseValue(value.english) || null,
});

/**
 * The whole agent, every list included: `updateAgent` replaces each list it is given, so sending
 * them all keeps create and update one shape. A blank url or model is sent as null, which clears it.
 */
export function draftToInput(draft: AgentDraft): CreateAgentDataInput {
  return {
    name: draft.name.trim(),
    vendor: draft.vendor.trim(),
    url: draft.url.trim() || null,
    model: draft.model.trim() || null,
    role: text(draft.role),
    covers: draft.covers.filter((c) => !blankText(c)).map(text),
    tools: draft.tools
      .filter((t) => !blank(t.name))
      .map((t) => ({ name: t.name.trim(), description: text(t.description) })),
    integrations: draft.integrations.map((i) => i.trim()).filter(Boolean),
    guardrails: draft.guardrails.filter((g) => !blankText(g)).map(text),
  };
}
